import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { ResumenNegocio, FichaBoliche } from './SuperAdminResumen';

const STATUS_META = {
  pending: { label: 'Sin verificar', cls: 'lb-status--gen' },
  trial: { label: 'Trial', cls: 'lb-status--paid' },
  active: { label: 'Activo', cls: 'lb-status--paid' },
  expired: { label: 'Vencido', cls: 'lb-status--used' },
  suspended: { label: 'Suspendido', cls: 'lb-status--used' },
};

// Situación de cobro de un boliche.
const SIT_META = {
  moroso: { label: 'Moroso', color: 'var(--red-400)', bg: 'rgba(200,16,46,0.12)' },
  por_vencer: { label: 'Por vencer', color: '#d4af37', bg: 'rgba(212,175,55,0.14)' },
  trial: { label: 'En prueba', color: '#5b9bd5', bg: 'rgba(91,155,213,0.14)' },
  al_dia: { label: 'Al día', color: '#2ecc71', bg: 'rgba(46,204,113,0.12)' },
};
const PLAN_LABEL = { basico: 'Básico', pro: 'Pro', full: 'Full' };

// Precios de la plataforma (editables). Orden y etiquetas de la pestaña Precios.
const PRICE_META = [
  { key: 'qr_unit', label: 'Por entrada (QR)', hint: 'Se cobra por cada entrada vendida', defRound: 100 },
  { key: 'plan_pro', label: 'Abono Pro', hint: 'Abono mensual del plan Pro', defRound: 1000 },
  { key: 'plan_full', label: 'Abono Full', hint: 'Abono mensual del plan Full', defRound: 1000 },
];
const ROUND_OPTS = [{ v: 0, l: 'Sin redondear' }, { v: 100, l: 'A $100' }, { v: 1000, l: 'A $1.000' }];
const roundTo = (n, step) => (step > 1 ? Math.round(Number(n) / step) * step : Math.round(Number(n)));

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return '—'; }
}
// Fecha para <input type="date"> (YYYY-MM-DD) a partir de hoy + n días.
function dateInput(d) {
  const x = d ? new Date(d) : new Date();
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}
function plusDays(n) { const x = new Date(); x.setDate(x.getDate() + n); return x; }
function money(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('es-AR');
}
function Pill({ meta }) {
  return <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, color: meta.color, background: meta.bg }}>{meta.label}</span>;
}

// FICHA DEL RESPONSABLE DE UN BOLICHE.
//
// Datos personales: se piden al abrir, no se precargan con la tabla, y se pueden
// corregir desde acá (queda marcado que lo tocó TukiPro y no el titular).
function FichaTitular({ org, api, onClose }) {
  const CAMPOS = [
    ['titular_nombre', 'Nombre y apellido'],
    ['titular_dni', 'DNI'],
    ['titular_telefono', 'Teléfono'],
    ['titular_razon_social', 'Razón social'],
    ['titular_cuit', 'CUIT'],
    ['titular_direccion', 'Domicilio'],
    ['titular_localidad', 'Localidad'],
    ['titular_cp', 'Código postal'],
  ];
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [editar, setEditar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/superadmin/orgs/${org.id}/titular`)
      .then(x => { setD(x); setF(Object.fromEntries(CAMPOS.map(([k]) => [k, x[k] || '']))); })
      .catch(e => setErr(e.message));
  }, [org.id]); // eslint-disable-line

  async function guardar() {
    setBusy(true); setErr('');
    try { const x = await api.put(`/api/superadmin/orgs/${org.id}/titular`, f); setD(x); setEditar(false); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  const falta = (d?.faltantes?.basicos || []).length + (d?.faltantes?.fiscales || []).length;
  const texto = CAMPOS.map(([k, l]) => `${l}: ${d?.[k] || '—'}`).join('\n');

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, padding: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Datos del titular</div>
            <div className="lb-h" style={{ fontSize: 18 }}>{org.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{org.slug}.tukipro.com · {org.owner_email || 'sin email'}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="lb-err" style={{ marginBottom: 12 }}>{err}</div>}
        {!d && !err && <div style={{ color: 'var(--ink-600)', fontSize: 13, padding: '14px 0' }}>Cargando…</div>}

        {d && (<>
          {falta > 0 && (
            <div style={{ border: '1px solid rgba(212,175,55,0.45)', background: 'rgba(212,175,55,0.07)', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#d4af37', lineHeight: 1.5 }}>
              Faltan {falta} dato(s). El titular los puede completar desde su panel, en Datos del titular.
            </div>
          )}

          {!editar && CAMPOS.map(([k, l]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 13 }}>
              <span style={{ color: 'var(--ink-700)' }}>{l}</span>
              <span style={{ color: d[k] ? '#fff' : 'var(--ink-600)', fontWeight: d[k] ? 600 : 400, textAlign: 'right', wordBreak: 'break-word' }}>{d[k] || 'Sin cargar'}</span>
            </div>
          ))}

          {editar && f && CAMPOS.map(([k, l]) => (
            <div key={k} style={{ marginBottom: 10 }}>
              <div className="lb-label" style={{ marginBottom: 4 }}>{l}</div>
              <input className="lb-input" value={f[k]} onChange={e => setF(s => ({ ...s, [k]: e.target.value }))} />
            </div>
          ))}

          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 12 }}>
            {d.titular_actualizado_at
              ? <>Última carga: {new Date(d.titular_actualizado_at).toLocaleDateString('es-AR')} · {d.titular_actualizado_por === 'superadmin' ? 'corregido por TukiPro' : d.titular_actualizado_por === 'alta' ? 'cargado en el alta' : 'cargado por el titular'}</>
              : 'Todavía no cargó ningún dato.'}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {editar
              ? (<>
                  <button className="lb-btn lb-btn--primary lb-btn--sm" disabled={busy} onClick={guardar}>{busy ? 'Guardando…' : 'Guardar'}</button>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { setEditar(false); setF(Object.fromEntries(CAMPOS.map(([k]) => [k, d[k] || '']))); }}>Cancelar</button>
                </>)
              : (<>
                  <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => setEditar(true)}>Corregir</button>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { try { navigator.clipboard.writeText(texto); } catch {} }}>Copiar ficha</button>
                </>)}
          </div>
        </>)}
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout, token } = useAuth();
  const api = useApi();
  const [tab, setTab] = useState('resumen');
  const [fichaId, setFichaId] = useState(null);   // ficha de un boliche abierta
  const [fichaVer, setFichaVer] = useState(0);    // sube cuando algo cambió el boliche de la ficha
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState('');
  const [qBusca, setQBusca] = useState('');   // q con un respiro: no se pide una página por tecla
  const [statusFilter, setStatusFilter] = useState(null); // filtro KPI Boliches (por status)
  const [planFiltro, setPlanFiltro] = useState('');
  const [orden, setOrden] = useState('recientes');
  const [pagina, setPagina] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [exportando, setExportando] = useState(false);
  const POR_PAGINA = 50;
  const [titularOrg, setTitularOrg] = useState(null);     // ficha del responsable abierta
  const [bonifOrg, setBonifOrg] = useState(null);         // modal de bonificación abierto
  const [cobrosFilter, setCobrosFilter] = useState(null); // filtro KPI Cobros (por situacion)
  const [loading, setLoading] = useState(true);
  const [del, setDel] = useState(null); // org a borrar
  const [confirmSlug, setConfirmSlug] = useState('');
  const [busy, setBusy] = useState(false);
  // Cobros
  const [cobros, setCobros] = useState(null);
  const [cobrosLoading, setCobrosLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { org, charges, adeudado }
  // Caja negra
  const [errs, setErrs] = useState(null);        // { errors, totals }
  const [errSource, setErrSource] = useState('');
  const [errDays, setErrDays] = useState(7);
  const [errLoading, setErrLoading] = useState(false);
  const [health, setHealth] = useState(null);    // salud de dispositivos de un evento
  const [healthEv, setHealthEv] = useState('');
  // Precios
  const [prices, setPrices] = useState(null);   // { qr_unit:{vigente,programado}, ... }
  const [draft, setDraft] = useState({});        // key -> { price:string, round:number }
  const [pct, setPct] = useState('');            // % de aumento a todos
  const [effAt, setEffAt] = useState(dateInput(plusDays(30)));
  const [notify, setNotify] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  // Override por boliche
  const [ovOrg, setOvOrg] = useState(null);      // { org, data:{current,global,overrides} }
  const [ovDraft, setOvDraft] = useState({});    // key -> string

  // El listado de boliches se pagina y filtra en el servidor: con cientos de boliches
  // no se puede traer todo para filtrar acá.
  useEffect(() => { const t = setTimeout(() => setQBusca(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPagina(1); }, [qBusca, statusFilter, planFiltro, orden]);
  const filtrosOrgs = () => {
    const p = new URLSearchParams({ orden });
    if (qBusca) p.set('q', qBusca);
    if (statusFilter) p.set('estado', statusFilter);
    if (planFiltro) p.set('plan', planFiltro);
    return p;
  };
  // Solo vale la respuesta del último pedido: al cambiar filtros rápido, una página vieja
  // que llega tarde no pisa a la nueva.
  const ultimoPedido = useRef(0);
  const load = useCallback(() => {
    setLoading(true);
    const n = ++ultimoPedido.current;
    const p = filtrosOrgs();
    p.set('pagina', String(pagina)); p.set('por', String(POR_PAGINA));
    Promise.all([
      api.get('/api/superadmin/stats').then(setStats).catch(() => {}),
      api.get(`/api/superadmin/orgs?${p}`).then(r => { if (n === ultimoPedido.current) { setOrgs(r.orgs || []); setTotalOrgs(r.total || 0); } }).catch(() => {}),
    ]).finally(() => { if (n === ultimoPedido.current) setLoading(false); });
  }, [qBusca, statusFilter, planFiltro, orden, pagina]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Exportar lo filtrado a un CSV que abre Excel. Va con fetch propio porque la respuesta
  // es un archivo, no JSON.
  async function exportarOrgs() {
    setExportando(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/superadmin/orgs/export?${filtrosOrgs()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('No se pudo exportar');
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = (r.headers.get('content-disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'boliches.csv';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(e.message); } finally { setExportando(false); }
  }

  const loadCobros = useCallback(() => {
    setCobrosLoading(true);
    api.get('/api/superadmin/cobros').then(setCobros).catch(() => {}).finally(() => setCobrosLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => { if (tab === 'cobros') loadCobros(); }, [tab, loadCobros]);

  // ── Caja negra ──
  const loadErrs = useCallback(() => {
    setErrLoading(true);
    const q = new URLSearchParams({ days: String(errDays) });
    if (errSource) q.set('source', errSource);
    api.get(`/api/superadmin/errors?${q}`).then(setErrs).catch(() => {}).finally(() => setErrLoading(false));
  }, [errDays, errSource]); // eslint-disable-line
  useEffect(() => { if (tab === 'errores') loadErrs(); }, [tab, loadErrs]);

  function verSalud(eventId) {
    setHealthEv(eventId); setHealth(null);
    api.get(`/api/superadmin/scanner-health?event_id=${eventId}`).then(setHealth).catch(e => alert(e.message));
  }

  // ── Precios de la plataforma ──
  const loadPrices = useCallback(() => {
    setPricesLoading(true);
    api.get('/api/superadmin/prices').then(p => {
      setPrices(p);
      // draft arranca con el precio vigente y el redondeo default de cada key
      const d = {};
      for (const m of PRICE_META) d[m.key] = { price: String(p[m.key]?.vigente?.price ?? ''), round: m.defRound };
      setDraft(d);
    }).catch(() => {}).finally(() => setPricesLoading(false));
  }, []); // eslint-disable-line
  useEffect(() => { if (tab === 'precios') loadPrices(); }, [tab, loadPrices]);

  // Aplica el % a TODOS los vigentes y redondea por precio (según su select).
  function applyPct() {
    const p = Number(pct);
    if (!Number.isFinite(p)) return;
    setDraft(prev => {
      const d = { ...prev };
      for (const m of PRICE_META) {
        const base = Number(prices?.[m.key]?.vigente?.price ?? 0);
        const nv = roundTo(base * (1 + p / 100), prev[m.key]?.round ?? m.defRound);
        d[m.key] = { ...prev[m.key], price: String(nv) };
      }
      return d;
    });
  }

  // Programa el aumento: manda solo las keys cuyo precio cambió respecto del vigente.
  async function schedulePrices() {
    const list = [];
    for (const m of PRICE_META) {
      const nv = Number(draft[m.key]?.price);
      const cur = Number(prices?.[m.key]?.vigente?.price ?? 0);
      if (Number.isFinite(nv) && nv >= 0 && nv !== cur) list.push({ key: m.key, price: nv });
    }
    if (!list.length) { alert('No cambiaste ningún precio.'); return; }
    const eff = new Date(effAt + 'T00:00:00');
    const days = Math.round((eff - Date.now()) / 86400000);
    const soon = days < 30;
    const resumen = list.map(x => `• ${PRICE_META.find(m => m.key === x.key).label}: ${money(prices[x.key]?.vigente?.price)} → ${money(x.price)}`).join('\n');
    if (!window.confirm(`Programar aumento para el ${fmt(eff)}${soon ? ` (¡en ${days} días, menos de 30!)` : ''}:\n\n${resumen}\n\n${notify ? 'Se avisará a todos los boliches (banner + email).' : 'SIN aviso a los boliches.'}`)) return;
    setSavingPrices(true);
    try {
      await api.post('/api/superadmin/prices/schedule', { prices: list, effective_at: eff.toISOString(), notify });
      alert('Aumento programado.');
      loadPrices();
    } catch (e) { alert(e.message); } finally { setSavingPrices(false); }
  }

  async function cancelScheduled(id) {
    if (!window.confirm('¿Cancelar este aumento programado? Los precios vigentes quedan como están.')) return;
    try { await api.del(`/api/superadmin/prices/${id}`); loadPrices(); }
    catch (e) { alert(e.message); }
  }

  // ── Override de precios por boliche ──
  async function openOverride(org) {
    setOvOrg({ org, data: null });
    try {
      const data = await api.get(`/api/superadmin/orgs/${org.id}/overrides`);
      setOvOrg({ org, data });
      const d = {}; for (const m of PRICE_META) d[m.key] = String(data.current?.[m.key] ?? '');
      setOvDraft(d);
    } catch (e) { alert(e.message); setOvOrg(null); }
  }
  async function saveOverride(key) {
    const price = Number(ovDraft[key]);
    if (!Number.isFinite(price) || price < 0) { alert('Precio inválido'); return; }
    const cur = Number(ovOrg.data.current?.[key] ?? 0);
    const raise = price > cur;
    if (raise && !window.confirm(`$${price} es MÁS que el precio vigente del boliche (${money(cur)}). Un aumento va con 30 días de aviso, no al instante. ¿Programarlo igual?`)) return;
    try {
      const r = await api.post(`/api/superadmin/orgs/${ovOrg.org.id}/overrides`, { key, price });
      alert(r.immediate ? 'Override aplicado al instante.' : `Aumento programado para el ${fmt(r.effective_at)} (con aviso al boliche).`);
      openOverride(ovOrg.org);
    } catch (e) { alert(e.message); }
  }
  async function removeOverride(oid) {
    if (!window.confirm('¿Quitar este override? El boliche vuelve al precio general.')) return;
    try { await api.del(`/api/superadmin/orgs/${ovOrg.org.id}/overrides/${oid}`); openOverride(ovOrg.org); }
    catch (e) { alert(e.message); }
  }

  async function setStatus(org, status) {
    const verb = status === 'suspended' ? 'suspender' : 'reactivar';
    if (!window.confirm(`¿Seguro que querés ${verb} "${org.name}" (${org.slug})?`)) return;
    try { await api.patch(`/api/superadmin/orgs/${org.id}`, { status }); load(); if (tab === 'cobros') loadCobros(); }
    catch (e) { alert(e.message); }
  }
  // Asignar / cambiar plan. Es el "pasar a plan pago": deja el trial y arranca a cobrar.
  async function setPlan(org, plan) {
    if (plan === org.plan) return;
    if (!window.confirm(`¿Poner a "${org.name}" en plan ${PLAN_LABEL[plan]}? Si estaba en prueba, pasa a plan pago y arranca el cobro.`)) return;
    try {
      await api.post(`/api/superadmin/orgs/${org.id}/plan`, { plan });
      load(); loadCobros();
      if (detail?.org?.id === org.id) refreshDetail(org.id);
    } catch (e) { alert(e.message); }
  }
  async function doDelete() {
    if (confirmSlug !== del.slug) return;
    setBusy(true);
    try { await api.del(`/api/superadmin/orgs/${del.id}`, { confirm_slug: confirmSlug }); setDel(null); setConfirmSlug(''); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  // ── Cobros: detalle por boliche ──
  async function openDetail(org) {
    setDetail({ org, charges: null, adeudado: null });
    try { const r = await api.get(`/api/superadmin/orgs/${org.id}/charges`); setDetail({ org, charges: r.charges || [], adeudado: r.adeudado || 0 }); }
    catch (e) { alert(e.message); setDetail(null); }
  }
  async function refreshDetail(orgId) {
    try { const r = await api.get(`/api/superadmin/orgs/${orgId}/charges`); setDetail(d => d && d.org.id === orgId ? { ...d, charges: r.charges || [], adeudado: r.adeudado || 0 } : d); }
    catch { /* noop */ }
  }
  async function markPaid(charge, status) {
    const to = status === 'paid' ? 'PAGADO' : 'impago';
    if (!window.confirm(`¿Marcar este cargo como ${to}?${status === 'paid' ? ' Si el boliche estaba bloqueado por este cargo, se desbloquea al instante.' : ''}`)) return;
    try {
      await api.patch(`/api/superadmin/charges/${charge.id}`, { status });
      await refreshDetail(detail.org.id);
      loadCobros();
      setFichaVer(v => v + 1);
    } catch (e) { alert(e.message); }
  }
  // Enviar el detalle de la deuda por WhatsApp (reusa el mecanismo de "pedidos").
  async function enviarDeuda(org) {
    let charges = [];
    try { const r = await api.get(`/api/superadmin/orgs/${org.id}/charges`); charges = (r.charges || []).filter(c => c.status === 'unpaid'); }
    catch (e) { alert(e.message); return; }
    if (!charges.length) { alert('Este boliche no tiene deuda pendiente.'); return; }
    const lines = charges.map(c => `• ${c.concept || c.type}: ${money(c.amount)}${c.due_at ? ` (vence ${fmt(c.due_at)})` : ''}`);
    const total = charges.reduce((a, c) => a + Number(c.amount), 0);
    const msg = `Hola ${org.name}! Te paso el detalle de tu cuenta en TUKIPRO:\n\n${lines.join('\n')}\n\nTotal adeudado: ${money(total)}\n\nCualquier cosa quedamos a disposición.`;
    const phone = (org.whatsapp || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  }

  const Kpi = ({ label, value, color, onClick, active }) => (
    <div
      className="lb-card"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      style={{
        padding: '16px 18px',
        cursor: onClick ? 'pointer' : 'default',
        border: active ? '1.5px solid #EC268F' : '1.5px solid transparent',
        boxShadow: active ? '0 0 0 3px rgba(236,38,143,0.15)' : 'none',
        transition: 'border-color .12s, box-shadow .12s',
      }}
    >
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, color: color || '#fff', marginTop: 4 }}>{value ?? '—'}</div>
    </div>
  );
  // Clic en un KPI: alterna el filtro (si ya está activo, lo saca).
  const toggleFilter = (key) => setStatusFilter(f => (f === key ? null : key));
  const shownOrgs = orgs; // ya vienen filtrados y paginados del servidor
  const paginas = Math.max(1, Math.ceil(totalOrgs / POR_PAGINA));
  const FILTER_LABEL = { active: 'Activos', trial: 'Trial', pending: 'Sin verificar', suspended: 'Suspendidos' };
  // Cobros: filtro por situación.
  const toggleCobros = (key) => setCobrosFilter(f => (f === key ? null : key));
  const shownCobros = cobrosFilter ? (cobros?.orgs || []).filter(o => o.situacion === cobrosFilter) : (cobros?.orgs || []);
  const TabBtn = ({ id, children }) => (
    <button
      className="lb-btn lb-btn--sm"
      onClick={() => setTab(id)}
      style={{ background: tab === id ? '#EC268F' : 'transparent', color: tab === id ? '#fff' : 'var(--ink-700)', border: tab === id ? 'none' : '1px solid var(--line)' }}
    >{children}</button>
  );
  const PlanSelect = ({ org }) => (
    <select className="lb-input" style={{ padding: '5px 8px', fontSize: 12, width: 'auto' }} value={org.plan || ''} onChange={e => setPlan(org, e.target.value)}>
      {!org.plan && <option value="" disabled>Plan…</option>}
      <option value="basico">Básico</option>
      <option value="pro">Pro · $50k</option>
      <option value="full">Full · $80k</option>
    </select>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '28px clamp(16px,4vw,40px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <div className="lb-eyebrow" style={{ color: '#EC268F' }}>TUKIPRO · Super-admin</div>
            <div className="lb-display" style={{ fontSize: 26 }}>{tab === 'resumen' ? 'Cómo viene TukiPro' : tab === 'cobros' ? 'Cobros' : tab === 'precios' ? 'Precios de la plataforma' : tab === 'errores' ? 'Errores y salud de dispositivos' : 'Boliches de la plataforma'}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-600)' }}>{user?.email}</span>
            <a className="lb-btn lb-btn--ghost lb-btn--sm" href="/admin/owner">← Mi panel</a>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={logout}>Salir</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <TabBtn id="resumen">Resumen</TabBtn>
          <TabBtn id="boliches">Boliches</TabBtn>
          <TabBtn id="cobros">Cobros</TabBtn>
          <TabBtn id="precios">Precios</TabBtn>
          <TabBtn id="errores">Errores</TabBtn>
        </div>

        {tab === 'resumen' && <ResumenNegocio api={api} onAbrir={setFichaId} />}

        {tab === 'boliches' && (<>
          {/* KPIs clicables (filtran el listado por estado) */}
          <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 12, marginBottom: 22 }}>
            <Kpi label="Total boliches" value={stats?.total} active={statusFilter === null} onClick={() => setStatusFilter(null)} />
            <Kpi label="Activos" value={stats?.active} color="#2ecc71" active={statusFilter === 'active'} onClick={() => toggleFilter('active')} />
            <Kpi label="Trial" value={stats?.trial} color="#2ecc71" active={statusFilter === 'trial'} onClick={() => toggleFilter('trial')} />
            <Kpi label="Sin verificar" value={stats?.pending} color="#d4af37" active={statusFilter === 'pending'} onClick={() => toggleFilter('pending')} />
            <Kpi label="Suspendidos" value={stats?.suspended} color="var(--red-400)" active={statusFilter === 'suspended'} onClick={() => toggleFilter('suspended')} />
          </div>

          {/* Búsqueda + chip de filtro activo */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="lb-input" style={{ maxWidth: 340 }} value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, subdominio o email de cualquier usuario…" />
            {q && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setQ('')}>Limpiar</button>}
            <select className="lb-input" style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }} value={planFiltro} onChange={e => setPlanFiltro(e.target.value)}>
              <option value="">Todos los planes</option>
              <option value="basico">Básico</option>
              <option value="pro">Pro</option>
              <option value="full">Full</option>
              <option value="exento">Exentos</option>
            </select>
            <select className="lb-input" style={{ width: 'auto', padding: '7px 10px', fontSize: 13 }} value={orden} onChange={e => setOrden(e.target.value)}>
              <option value="recientes">Más nuevos primero</option>
              <option value="antiguos">Más viejos primero</option>
              <option value="nombre">Por nombre</option>
            </select>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginLeft: 'auto' }} disabled={exportando} onClick={exportarOrgs}>
              {exportando ? 'Exportando…' : 'Exportar a Excel'}
            </button>
            {statusFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-700)', background: 'rgba(236,38,143,0.12)', border: '1px solid rgba(236,38,143,0.4)', borderRadius: 999, padding: '4px 10px' }}>
                Filtro: <b style={{ color: '#fff' }}>{FILTER_LABEL[statusFilter] || statusFilter}</b>
                <button className="lb-iconbtn" style={{ padding: 0, width: 16, height: 16, lineHeight: 1 }} title="Quitar filtro" onClick={() => setStatusFilter(null)}>✕</button>
              </span>
            )}
          </div>

          {/* Tabla boliches */}
          <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1.1fr 1.7fr' }}>
              <span>Boliche</span><span>Estado</span><span>Plan</span><span>Actividad</span><span></span>
            </div>
            {loading && <div style={{ padding: '20px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}
            {!loading && shownOrgs.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13 }}>{statusFilter || q ? 'Ningún boliche con ese filtro.' : 'Sin boliches.'}</div>}
            {shownOrgs.map(o => {
              const st = STATUS_META[o.status] || { label: o.status, cls: 'lb-status--gen' };
              return (
                <div key={o.id} className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 1.1fr 1.7fr' }}>
                  <div>
                    <button className="sa-nombre" onClick={() => setFichaId(o.id)} title="Ver ficha">{o.name}</button>
                    <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>{o.slug}.tukipro.com</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{o.owner_email || '—'}</div>
                    {/* La ficha del responsable: con el email solo no se puede facturar
                        ni reclamar nada. Se pide aparte porque son datos personales y no
                        tienen por qué viajar en cada refresco de esta tabla. */}
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 6 }} onClick={() => setTitularOrg(o)}>Ver datos del titular</button>
                  </div>
                  <span><span className={'lb-status ' + st.cls}>{st.label}</span></span>
                  <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
                    {o.plan ? <>{PLAN_LABEL[o.plan]}{o.plan_price > 0 ? ` · ${money(o.plan_price)}/mes` : ''}</> : <span style={{ color: 'var(--ink-600)' }}>Exento</span>}
                    {o.bonif_pct != null && <div style={{ color: '#2ecc71', fontWeight: 600 }}>{bonifTexto(o.bonif_pct, o.bonif_hasta)}</div>}
                    {o.plan_pending && <div style={{ color: '#d4af37' }}>Pasa a {PLAN_LABEL[o.plan_pending]} el {fmt(o.plan_pending_at)}</div>}
                    <div style={{ color: 'var(--ink-600)' }}>Alta {fmt(o.created_at)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
                    {o.events} ev · {o.tickets} entr.
                    {o.adeudado > 0 && <div style={{ color: '#d4af37', fontWeight: 600 }}>Debe {money(o.adeudado)}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                    {o.plan && <PlanSelect org={o} />}
                    {o.plan && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setBonifOrg(o)}>Bonificar</button>}
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => openOverride(o)}>Precio</button>
                    {o.status === 'suspended'
                      ? <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setStatus(o, 'active')}>Reactivar</button>
                      : <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setStatus(o, 'suspended')}>Suspender</button>}
                    <button className="lb-iconbtn" title="Eliminar" onClick={() => { setDel(o); setConfirmSlug(''); }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Paginado */}
          {totalOrgs > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12, fontSize: 12.5, color: 'var(--ink-700)', flexWrap: 'wrap' }}>
              <span>Mostrando {(pagina - 1) * POR_PAGINA + 1}–{Math.min(pagina * POR_PAGINA, totalOrgs)} de {totalOrgs.toLocaleString('es-AR')}</span>
              {paginas > 1 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagina <= 1 || loading} onClick={() => setPagina(p => p - 1)}>← Anterior</button>
                  <span>Página {pagina} de {paginas}</span>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagina >= paginas || loading} onClick={() => setPagina(p => p + 1)}>Siguiente →</button>
                </div>
              )}
            </div>
          )}
        </>)}

        {tab === 'cobros' && (<>
          {/* KPIs de cobranza (clicables: filtran por situación) */}
          <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 12, marginBottom: 22 }}>
            <Kpi label="Total adeudado" value={money(cobros?.total_adeudado)} color="#EC268F" active={cobrosFilter === null} onClick={() => setCobrosFilter(null)} />
            <Kpi label="Morosos" value={cobros?.morosos} color="var(--red-400)" active={cobrosFilter === 'moroso'} onClick={() => toggleCobros('moroso')} />
            <Kpi label="Por vencer" value={cobros?.por_vencer} color="#d4af37" active={cobrosFilter === 'por_vencer'} onClick={() => toggleCobros('por_vencer')} />
            <Kpi label="En prueba" value={cobros?.en_trial} color="#5b9bd5" active={cobrosFilter === 'trial'} onClick={() => toggleCobros('trial')} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: 'var(--ink-600)', marginBottom: 12 }}>
            <span>Solo boliches con plan o cargos. Los exentos (como lobbo) no facturan y no aparecen acá.</span>
            {cobrosFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-700)', background: 'rgba(236,38,143,0.12)', border: '1px solid rgba(236,38,143,0.4)', borderRadius: 999, padding: '4px 10px' }}>
                Filtro: <b style={{ color: '#fff' }}>{(SIT_META[cobrosFilter] || {}).label || cobrosFilter}</b>
                <button className="lb-iconbtn" style={{ padding: 0, width: 16, height: 16, lineHeight: 1 }} title="Quitar filtro" onClick={() => setCobrosFilter(null)}>✕</button>
              </span>
            )}
          </div>

          {/* Tabla cobros */}
          <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.5fr 1fr 0.9fr 1fr 1.9fr' }}>
              <span>Boliche</span><span>Situación</span><span>Adeudado</span><span>Próx. venc.</span><span></span>
            </div>
            {cobrosLoading && <div style={{ padding: '20px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}
            {!cobrosLoading && shownCobros.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13 }}>{cobrosFilter ? 'Ningún boliche con ese filtro.' : 'Ningún boliche en el circuito de facturación todavía.'}</div>}
            {shownCobros.map(o => {
              const sit = SIT_META[o.situacion] || SIT_META.al_dia;
              const prox = o.prox_due;
              return (
                <div key={o.id} className="lb-row" style={{ gridTemplateColumns: '1.5fr 1fr 0.9fr 1fr 1.9fr' }}>
                  <div>
                    <button className="sa-nombre" onClick={() => setFichaId(o.id)} title="Ver ficha">{o.name}</button>
                    <div style={{ fontSize: 11, color: 'var(--ink-700)' }}>
                      {o.plan ? <b style={{ color: '#cdd' }}>{PLAN_LABEL[o.plan]}</b> : 'Exento'}
                      {o.plan_price > 0 ? ` · abono ${money(o.plan_price)}/mes` : o.plan === 'basico' ? ' · solo QR' : ''}
                    </div>
                    {o.bonif_pct != null && <div style={{ fontSize: 11, color: '#2ecc71', fontWeight: 600 }}>{bonifTexto(o.bonif_pct, o.bonif_hasta)}</div>}
                    {o.avisados > 0 && <div style={{ fontSize: 11, color: '#d4af37', fontWeight: 700 }}>Avisó que pagó {o.avisados === 1 ? 'un cargo' : o.avisados + ' cargos'}</div>}
                  </div>
                  <span><Pill meta={sit} /></span>
                  <div style={{ fontSize: 14, fontWeight: 700, color: o.adeudado > 0 ? '#fff' : 'var(--ink-600)' }}>{money(o.adeudado)}</div>
                  <div style={{ fontSize: 12, color: o.situacion === 'moroso' ? 'var(--red-400)' : 'var(--ink-700)' }}>
                    {prox ? (o.cargo_vencido ? 'Venció ' : 'Vence ') + fmt(prox) : '—'}
                    {o.cargos_impagos > 0 && <div style={{ color: 'var(--ink-600)', fontSize: 11 }}>{o.cargos_impagos} cargo(s)</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                    <PlanSelect org={o} />
                    <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => openDetail(o)}>Ver cargos</button>
                    <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => enviarDeuda(o)}>Enviar deuda ↗</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>)}

        {tab === 'errores' && (<>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 14, maxWidth: 720, lineHeight: 1.5 }}>
            Registro técnico de fallas. Los errores repetidos se <b style={{ color: '#fff' }}>agrupan</b> (la columna "veces" dice cuántas). No contienen datos personales ni códigos QR: se limpian antes de guardarse. Se borran solos a los 90 días (los datos de rendimiento, a los 30).
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="lb-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} value={errSource} onChange={e => setErrSource(e.target.value)}>
              <option value="">Todo</option>
              <option value="backend">Servidor</option>
              <option value="scanner">Escáner de puerta</option>
              <option value="tpv">TPV / caja</option>
            </select>
            <select className="lb-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }} value={errDays} onChange={e => setErrDays(Number(e.target.value))}>
              <option value={1}>Últimas 24 h</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={loadErrs}>Actualizar</button>
            {(errs?.totals || []).map(t => (
              <span key={t.source} style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-700)', border: '1px solid var(--line)', borderRadius: 999, padding: '4px 10px' }}>
                {t.source === 'backend' ? 'servidor' : t.source === 'scanner' ? 'escáner' : t.source}: <b style={{ color: '#fff' }}>{t.n}</b>
              </span>
            ))}
          </div>

          <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '0.7fr 1fr 2.2fr 1.1fr 0.5fr 1fr' }}>
              <span>Origen</span><span>Tipo</span><span>Qué pasó</span><span>Boliche / evento</span><span>Veces</span><span>Última vez</span>
            </div>
            {errLoading && <div style={{ padding: '20px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}
            {!errLoading && (errs?.errors || []).length === 0 && (
              <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: '#2ecc71' }}>Sin errores registrados en el período. 👌</div>
            )}
            {(errs?.errors || []).map(e => {
              const col = e.source === 'scanner' ? '#EC268F' : e.source === 'tpv' ? '#5b9bd5' : '#d4af37';
              const org = e.org_name || (e.org_slug ? e.org_slug : '—');
              return (
                <div key={e.id} className="lb-row" style={{ gridTemplateColumns: '0.7fr 1fr 2.2fr 1.1fr 0.5fr 1fr', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: col }}>
                    {e.source === 'backend' ? 'servidor' : e.source === 'scanner' ? 'escáner' : e.source}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff' }}>{e.kind}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--ink-800)', wordBreak: 'break-word' }}>{e.message || '—'}</div>
                    {e.route && <div style={{ fontSize: 11, color: 'var(--ink-600)', fontFamily: 'monospace', marginTop: 2 }}>{e.route}</div>}
                    {e.context && <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>{Object.entries(e.context).map(([k, v]) => `${k}=${v}`).join(' · ')}</div>}
                    {/* DE QUIÉN fue el error. Los errores se agrupan por hora, así que se
                        listan los dispositivos distintos que cayeron en el mismo grupo.
                        Sin esto, "camera_fail 11 veces" no dice a quién ir a ayudar: en
                        una fiesta con 28 teléfonos escaneando es la diferencia entre un
                        número y una acción. */}
                    {(e.puesto || (e.devices || []).length > 0) && (
                      <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 3 }}>
                        {e.puesto && <b style={{ color: '#fff' }}>{e.puesto}</b>}
                        {e.puesto && (e.devices || []).length > 0 && ' · '}
                        {(e.devices || []).length > 0 && (
                          <span style={{ fontFamily: 'monospace', fontSize: 10.5 }}>
                            {(e.devices || []).slice(0, 6).join(' ')}
                            {e.devices.length > 6 && ` +${e.devices.length - 6}`}
                          </span>
                        )}
                        {(e.devices || []).length > 1 && (
                          <span style={{ color: 'var(--ink-600)' }}> · {e.devices.length} dispositivos</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-700)', minWidth: 0 }}>
                    <div style={{ color: '#fff' }}>{org}</div>
                    {e.event_name && <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>{e.event_name}</div>}
                  </div>
                  <span style={{ fontWeight: 700, color: e.count > 10 ? 'var(--red-400)' : '#fff', fontSize: 14 }}>{e.count}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>
                    {new Date(e.last_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Salud de los dispositivos de una noche */}
          <div className="lb-eyebrow" style={{ margin: '26px 0 8px' }}>Salud de los dispositivos por evento</div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 10, maxWidth: 720, lineHeight: 1.5 }}>
            Cada teléfono que escanea reporta cómo viene cada minuto. <b style={{ color: '#fff' }}>Hilo ocupado</b> es lo que hay que mirar: por encima del 60% el teléfono empieza a trabarse.
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <select className="lb-input" style={{ maxWidth: 320, padding: '6px 10px', fontSize: 13 }} value={healthEv} onChange={e => e.target.value && verSalud(e.target.value)}>
              <option value="">Elegí un evento…</option>
              {[...new Map((errs?.errors || []).filter(x => x.event_name).map(x => [x.event_name, x])).values()].map((x, i) => (
                <option key={i} value={x.event_id || ''}>{x.event_name} · {x.org_name || ''}</option>
              ))}
            </select>
            <input className="lb-input" style={{ maxWidth: 300, padding: '6px 10px', fontSize: 13 }} placeholder="…o pegá el ID del evento" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) verSalud(e.target.value.trim()); }} />
          </div>
          {health && (
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 20px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.1fr 0.7fr 0.8fr 0.9fr 0.9fr 0.8fr 1fr' }}>
                <span>Dispositivo</span><span>Escaneos</span><span>Hilo (últ / peor)</span><span>p95 ms</span><span>Análisis/s</span><span>Sin sincr.</span><span>Último latido</span>
              </div>
              {(health.devices || []).length === 0 && <div style={{ padding: '20px 0', fontSize: 13, color: 'var(--ink-600)' }}>Este evento no tiene latidos registrados.</div>}
              {(health.devices || []).map(d => {
                const occ = d.occupancy == null ? null : Number(d.occupancy);
                const peorOcc = d.peor?.max_occupancy == null ? null : Number(d.peor.max_occupancy);
                const cOf = (v) => v == null ? 'var(--ink-600)' : v > 60 ? 'var(--red-400)' : v > 40 ? '#d4af37' : '#2ecc71';
                return (
                  <div key={(d.dev || d.session_hash) + d.source} className="lb-row" style={{ gridTemplateColumns: '1.1fr 0.7fr 0.8fr 0.9fr 0.9fr 0.8fr 1fr', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      {/* El nombre del puesto va PRIMERO y el id abajo: con 28 teléfonos
                          en la puerta, "Pedro" sirve y "6ajbjgfz" no. Sale del link, que
                          se crea a nombre de alguien; si el link es de los viejos (sin
                          nombre) queda solo el id, como antes. */}
                      <div style={{ color: '#fff', fontSize: 13 }}>
                        {d.puesto || d.device_label || '—'} {d.source === 'tpv' && <span style={{ color: '#5b9bd5', fontSize: 11 }}>· TPV</span>}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--ink-600)' }}>
                        {(d.dev || d.session_hash || '').slice(0, 8)}{d.puesto ? ` · ${d.device_label || ''}` : ''} · {d.cam_res || ''}
                      </div>
                      {d.peor?.latidos_offline > 0 && <div style={{ fontSize: 10, color: 'var(--red-400)' }}>{d.peor.latidos_offline} min sin conexión</div>}
                      {/* Padrón viejo = ese teléfono validó contra una lista desactualizada:
                          no conoce las ventas de boletería de la noche ni los QR borrados. */}
                      {d.peor?.max_padron_age > 900 && (
                        <div style={{ fontSize: 10, color: d.peor.max_padron_age > 2400 ? 'var(--red-400)' : '#d4af37' }}>
                          padrón de hasta {Math.round(d.peor.max_padron_age / 60)} min
                        </div>
                      )}
                    </div>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{d.scans_total ?? '—'}</span>
                    <span style={{ fontWeight: 700 }}>
                      <span style={{ color: cOf(occ) }}>{occ == null ? '—' : occ + '%'}</span>
                      <span style={{ color: 'var(--ink-600)' }}> / </span>
                      <span style={{ color: cOf(peorOcc) }}>{peorOcc == null ? '—' : peorOcc + '%'}</span>
                    </span>
                    <span style={{ color: 'var(--ink-800)' }}>{d.ms_p95 ?? '—'}{d.peor?.max_p95 ? <span style={{ color: 'var(--ink-600)' }}> / {Number(d.peor.max_p95)}</span> : null}</span>
                    <span style={{ color: 'var(--ink-800)' }}>{d.aps ?? '—'}</span>
                    <span style={{ color: d.queue_pending > 0 ? '#d4af37' : 'var(--ink-600)' }}>{d.queue_pending ?? '—'}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>
                      {new Date(d.at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {d.peor?.latidos ? <div style={{ fontSize: 10, color: 'var(--ink-600)' }}>{d.peor.latidos} latidos</div> : null}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {health && <LineaDeTiempo timeline={health.timeline || []} />}
        </>)}

        {tab === 'precios' && (<>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 16, maxWidth: 640, lineHeight: 1.5 }}>
            Los precios rigen para <b style={{ color: '#fff' }}>todos los boliches</b> (salvo los que tengan un precio propio). Un aumento entra en vigencia en la fecha que elijas (default +30 días) — los cargos ya emitidos <b style={{ color: '#fff' }}>no cambian</b>.
          </div>
          {pricesLoading && <div style={{ padding: '20px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}

          {prices && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px,360px)', gap: 20, alignItems: 'start' }}>
              {/* Columna izquierda: edición de precios */}
              <div className="lb-card" style={{ padding: '18px 20px' }}>
                <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Nuevos precios</div>

                {/* Aumento por % a todos */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--line)' }}>
                  <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>Aumentar todo</span>
                  <input className="lb-input" style={{ width: 90 }} type="number" value={pct} onChange={e => setPct(e.target.value)} placeholder="%" />
                  <span style={{ fontSize: 13, color: 'var(--ink-600)' }}>%</span>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={applyPct} disabled={pct === ''}>Previsualizar ↓</button>
                  <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>Calcula sobre el vigente y redondea; después podés ajustar a mano.</span>
                </div>

                {PRICE_META.map(m => {
                  const vig = prices[m.key]?.vigente?.price;
                  const nv = Number(draft[m.key]?.price);
                  const changed = Number.isFinite(nv) && nv !== Number(vig);
                  const pctShown = changed && Number(vig) > 0 ? Math.round(((nv - vig) / vig) * 100) : null;
                  return (
                    <div key={m.key} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{m.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>Vigente {money(vig)}{m.key === 'qr_unit' ? ' · por entrada' : '/mes'}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: 'var(--ink-600)' }}>$</span>
                        <input className="lb-input" style={{ width: 110 }} type="number" value={draft[m.key]?.price ?? ''}
                          onChange={e => setDraft(d => ({ ...d, [m.key]: { ...d[m.key], price: e.target.value } }))} />
                      </div>
                      <select className="lb-input" style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }} value={draft[m.key]?.round ?? m.defRound}
                        onChange={e => setDraft(d => ({ ...d, [m.key]: { ...d[m.key], round: Number(e.target.value) } }))}>
                        {ROUND_OPTS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                      <div style={{ width: 64, textAlign: 'right', fontSize: 12, fontWeight: 700, color: changed ? '#EC268F' : 'var(--ink-600)' }}>
                        {changed ? (pctShown >= 0 ? '+' : '') + pctShown + '%' : '='}
                      </div>
                    </div>
                  );
                })}

                {/* Fecha + aviso + programar */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
                  <label style={{ fontSize: 13, color: 'var(--ink-700)' }}>Entra en vigencia
                    <input className="lb-input" style={{ width: 'auto', marginLeft: 8 }} type="date" value={effAt} min={dateInput(new Date())} onChange={e => setEffAt(e.target.value)} />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-700)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} /> Avisar a los boliches (banner + email)
                  </label>
                  <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginLeft: 'auto' }} onClick={schedulePrices} disabled={savingPrices}>
                    {savingPrices ? 'Programando…' : 'Programar aumento'}
                  </button>
                </div>
              </div>

              {/* Columna derecha: estado actual (vigente + programado) */}
              <div className="lb-card" style={{ padding: '18px 20px' }}>
                <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Estado actual</div>
                {PRICE_META.map(m => {
                  const p = prices[m.key];
                  return (
                    <div key={m.key} style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 2 }}>
                        Vigente <b style={{ color: '#fff' }}>{money(p?.vigente?.price)}</b>
                        {p?.vigente?.since && <span style={{ color: 'var(--ink-600)' }}> · desde {fmt(p.vigente.since)}</span>}
                      </div>
                      {p?.programado && (
                        <div style={{ fontSize: 12, color: '#d4af37', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          Programado <b>{money(p.programado.price)}</b> desde {fmt(p.programado.effective_at)}
                          <button className="lb-iconbtn" style={{ padding: 0, width: 16, height: 16, lineHeight: 1 }} title="Cancelar" onClick={() => cancelScheduled(p.programado.id)}>✕</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DatosCobro api={api} />
        </>)}
      </div>

      {/* Ficha del boliche. Va antes que los demás modales: los que se abren desde ella
          (bonificar, precio, cargos, titular) quedan encima. */}
      {fichaId && (
        <FichaBoliche orgId={fichaId} api={api} version={fichaVer} onClose={() => setFichaId(null)}
          acciones={{ bonificar: setBonifOrg, precio: openOverride, cargos: openDetail, titular: setTitularOrg }} />
      )}

      {/* Modal override de precios por boliche */}
      {ovOrg && (
        <div className="lb-modal-backdrop" onClick={() => setOvOrg(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, padding: 26 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
              <div>
                <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Precio propio · {ovOrg.org.slug}</div>
                <div className="lb-h" style={{ fontSize: 18 }}>{ovOrg.org.name}</div>
              </div>
              <button className="lb-iconbtn" onClick={() => setOvOrg(null)}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5, marginBottom: 14 }}>
              Fijá un precio solo para este boliche (respetar un precio viejo o un acuerdo). Si es <b style={{ color: '#fff' }}>≤ el que ya paga</b>, aplica al instante; si es <b style={{ color: '#fff' }}>mayor</b>, va con 30 días de aviso.
            </div>
            {!ovOrg.data && <div style={{ padding: '16px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}
            {ovOrg.data && PRICE_META.map(m => {
              const cur = ovOrg.data.current?.[m.key];
              const glob = ovOrg.data.global?.[m.key];
              const hasOv = (ovOrg.data.overrides || []).find(o => o.key === m.key && new Date(o.effective_at) <= new Date());
              return (
                <div key={m.key} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '11px 0', borderTop: '1px solid var(--line)', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>
                      Paga {money(cur)}{hasOv ? ' (propio)' : ''} · general {money(glob)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: 'var(--ink-600)' }}>$</span>
                    <input className="lb-input" style={{ width: 100 }} type="number" value={ovDraft[m.key] ?? ''}
                      onChange={e => setOvDraft(d => ({ ...d, [m.key]: e.target.value }))} />
                  </div>
                  <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => saveOverride(m.key)}>Guardar</button>
                  {hasOv && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => removeOverride(hasOv.id)}>Quitar</button>}
                </div>
              );
            })}
            {/* Programados del override (aumentos futuros) */}
            {ovOrg.data && (ovOrg.data.overrides || []).filter(o => new Date(o.effective_at) > new Date()).map(o => (
              <div key={o.id} style={{ fontSize: 12, color: '#d4af37', marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                {PRICE_META.find(m => m.key === o.key)?.label}: programado {money(o.price)} desde {fmt(o.effective_at)}
                <button className="lb-iconbtn" style={{ padding: 0, width: 16, height: 16, lineHeight: 1 }} title="Cancelar" onClick={() => removeOverride(o.id)}>✕</button>
              </div>
            ))}
            <div style={{ marginTop: 20 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setOvOrg(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle de cargos de un boliche */}
      {detail && (
        <div className="lb-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, padding: 26 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Cargos · {detail.org.slug}</div>
                <div className="lb-h" style={{ fontSize: 18 }}>{detail.org.name}</div>
              </div>
              <button className="lb-iconbtn" onClick={() => setDetail(null)}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: 20, margin: '10px 0 16px', fontSize: 13, flexWrap: 'wrap' }}>
              <div><span style={{ color: 'var(--ink-600)' }}>Adeudado: </span><b style={{ color: detail.adeudado > 0 ? 'var(--red-400)' : '#2ecc71' }}>{money(detail.adeudado)}</b></div>
              {detail.org.plan && <div><span style={{ color: 'var(--ink-600)' }}>Plan: </span><b style={{ color: '#fff' }}>{PLAN_LABEL[detail.org.plan]}{detail.org.plan_price > 0 ? ` · ${money(detail.org.plan_price)}/mes` : ''}</b></div>}
            </div>

            {detail.charges === null && <div style={{ padding: '16px 0', color: 'var(--ink-600)', fontSize: 13 }}>Cargando cargos…</div>}
            {detail.charges && detail.charges.length === 0 && <div style={{ padding: '16px 0', color: 'var(--ink-600)', fontSize: 13 }}>Este boliche no tiene cargos todavía.</div>}

            {detail.charges && detail.charges.length > 0 && (
              <div className="tk-scroll-x" style={{ maxHeight: 360, overflowY: 'auto' }}>
                {detail.charges.map(c => {
                  const chMeta = c.status === 'paid'
                    ? { label: 'Pagado', color: '#2ecc71', bg: 'rgba(46,204,113,0.12)' }
                    : c.status === 'bonificado' ? { label: 'Bonificado', color: '#5b9bd5', bg: 'rgba(91,155,213,0.14)' }
                    : c.overdue ? { label: 'Vencido', color: 'var(--red-400)', bg: 'rgba(200,16,46,0.12)' }
                    : { label: 'Impago', color: '#d4af37', bg: 'rgba(212,175,55,0.14)' };
                  const tipo = c.type === 'subscription' ? 'Abono' : 'QR';
                  return (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderTop: '1px solid var(--line)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                          <span style={{ color: c.type === 'subscription' ? '#5b9bd5' : '#EC268F', fontSize: 11, marginRight: 6 }}>{tipo}</span>
                          {c.concept || (c.type === 'event_qr' ? 'Cargo por evento' : c.type)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
                          {c.qr_count != null && `${c.qr_count} entradas × ${money(c.unit_price)} · `}
                          {c.due_at && (c.status === 'paid' ? `pagado ${fmt(c.paid_at)}${c.pagado_por === 'mercadopago' ? ' con Mercado Pago' : ''}` : c.status === 'bonificado' ? '' : `vence ${fmt(c.due_at)}`)}
                          {c.bonif_pct != null && ` · bonificado ${Number(c.bonif_pct)}% de ${money(c.gross_amount)}`}
                        </div>
                        {c.status === 'unpaid' && c.pago_avisado_at && <div style={{ fontSize: 11, color: '#d4af37', fontWeight: 700, marginTop: 2 }}>Avisó que pagó el {fmt(c.pago_avisado_at)}</div>}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', whiteSpace: 'nowrap' }}>{money(c.amount)}</div>
                      <Pill meta={chMeta} />
                      {c.status === 'unpaid'
                        ? <button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => markPaid(c, 'paid')}>Marcar pagado</button>
                        : c.status === 'paid' ? <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => markPaid(c, 'unpaid')}>Revertir</button> : null}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setDetail(null)}>Cerrar</button>
              <button className="lb-btn lb-btn--primary lb-btn--block" onClick={() => enviarDeuda(detail.org)}>Enviar deuda por WhatsApp ↗</button>
            </div>
          </div>
        </div>
      )}

      {titularOrg && <FichaTitular org={titularOrg} api={api} onClose={() => setTitularOrg(null)} />}
      {bonifOrg && <ModalBonificacion org={bonifOrg} api={api} onClose={() => setBonifOrg(null)} onGuardado={() => { load(); if (tab === 'cobros') loadCobros(); setFichaVer(v => v + 1); }} />}

      {del && (
        <div className="lb-modal-backdrop" onClick={() => setDel(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, padding: 26 }}>
            <div className="lb-h" style={{ fontSize: 18, marginBottom: 8, color: 'var(--red-400)' }}>Eliminar boliche</div>
            <div style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
              Vas a <b style={{ color: '#fff' }}>borrar definitivamente</b> "{del.name}" y <b style={{ color: '#fff' }}>TODOS sus datos</b> (eventos, entradas, ventas, usuarios, inventario…). <b style={{ color: 'var(--red-400)' }}>No se puede deshacer.</b>
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', margin: '14px 0 6px' }}>Escribí <b style={{ color: '#fff' }}>{del.slug}</b> para confirmar:</div>
            <input className="lb-input" value={confirmSlug} onChange={e => setConfirmSlug(e.target.value)} placeholder={del.slug} autoFocus />
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setDel(null)}>Cancelar</button>
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ background: confirmSlug === del.slug ? 'var(--red-600)' : undefined }} disabled={confirmSlug !== del.slug || busy} onClick={doDelete}>{busy ? 'Eliminando…' : 'Eliminar definitivamente'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// LÍNEA DE TIEMPO DE LATIDOS. Existe por una noche concreta: el 8/8 hubo 15
// scan_timeout a las 02:27 y el panel solo mostraba el último latido y el peor de la
// noche, así que no se podía saber si el teléfono que estaba al 80% de hilo era el que
// estaba en la puerta a ESA hora. Con la hora al lado, eso se responde de un vistazo.
//
// Se agrupa por franja de 10 minutos y se muestra el PEOR valor de cada franja: 400
// latidos en una tabla no se leen, y lo que importa de una franja es su peor momento.
function LineaDeTiempo({ timeline }) {
  const [abierto, setAbierto] = useState(false);
  if (!timeline.length) return null;

  const franjas = {};
  for (const h of timeline) {
    const d = new Date(h.at);
    d.setMinutes(Math.floor(d.getMinutes() / 10) * 10, 0, 0);
    const k = d.toISOString();
    const f = franjas[k] || (franjas[k] = { at: d, occ: null, p95: null, aps: null, pend: 0, offline: 0, devs: new Set(), n: 0 });
    f.n++;
    f.devs.add(h.dev);
    if (h.online === false) f.offline++;
    const num = (v) => (v == null ? null : Number(v));
    const occ = num(h.occupancy), p95 = num(h.ms_p95), aps = num(h.aps), pend = num(h.queue_pending) || 0;
    if (occ != null && (f.occ == null || occ > f.occ)) f.occ = occ;      // peor ocupación
    if (p95 != null && (f.p95 == null || p95 > f.p95)) f.p95 = p95;      // peor pico
    if (aps != null && (f.aps == null || aps < f.aps)) f.aps = aps;      // menos análisis/s
    if (pend > f.pend) f.pend = pend;                                    // mayor cola
  }
  const filas = Object.values(franjas).sort((a, b) => a.at - b.at);
  const cOf = (v) => v == null ? 'var(--ink-600)' : v > 60 ? 'var(--red-400)' : v > 40 ? '#d4af37' : '#2ecc71';
  const hhmm = (d) => d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  // Franjas donde algo se vio mal: son las que hay que mirar primero.
  const malas = filas.filter(f => (f.occ != null && f.occ > 60) || (f.p95 != null && f.p95 > 100) || f.pend > 0 || f.offline > 0);

  return (
    <div className="lb-card" style={{ padding: '16px 20px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="lb-eyebrow">Línea de tiempo · franjas de 10 minutos</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 4 }}>
            Se muestra el <b style={{ color: '#fff' }}>peor</b> valor de cada franja. {malas.length > 0
              ? <span style={{ color: 'var(--red-400)' }}>{malas.length} franja{malas.length === 1 ? '' : 's'} con algo para mirar.</span>
              : 'Ninguna franja con problemas.'}
          </div>
        </div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setAbierto(o => !o)}>
          {abierto ? 'Ocultar' : `Ver las ${filas.length} franjas`}
        </button>
      </div>
      {abierto && (
        <div className="tk-scroll-x" style={{ marginTop: 14 }}>
          <div className="lb-row lb-th" style={{ gridTemplateColumns: '0.7fr 0.8fr 0.8fr 0.8fr 0.7fr 0.9fr' }}>
            <span>Hora</span><span>Hilo peor</span><span>p95 ms</span><span>Análisis/s mín</span><span>Cola</span><span>Equipos</span>
          </div>
          {filas.map((f, i) => {
            const mala = (f.occ != null && f.occ > 60) || (f.p95 != null && f.p95 > 100) || f.pend > 0 || f.offline > 0;
            return (
              <div key={i} className="lb-row" style={{ gridTemplateColumns: '0.7fr 0.8fr 0.8fr 0.8fr 0.7fr 0.9fr', background: mala ? 'rgba(200,16,46,0.10)' : undefined }}>
                <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: 12 }}>{hhmm(f.at)}</span>
                <span style={{ color: cOf(f.occ), fontWeight: 700 }}>{f.occ == null ? '—' : f.occ + '%'}</span>
                <span style={{ color: f.p95 > 100 ? 'var(--red-400)' : 'var(--ink-800)' }}>{f.p95 == null ? '—' : Math.round(f.p95)}</span>
                <span style={{ color: 'var(--ink-800)' }}>{f.aps == null ? '—' : f.aps}</span>
                <span style={{ color: f.pend > 0 ? '#d4af37' : 'var(--ink-600)' }}>{f.pend || '—'}</span>
                <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>
                  {f.devs.size}{f.offline > 0 && <span style={{ color: 'var(--red-400)' }}> · {f.offline} sin red</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Texto corto de una bonificación para las tablas: "Bonif. 100% · siempre".
// `hasta` se guarda como el comienzo del día siguiente; se muestra el último día incluido.
function bonifTexto(pct, hasta) {
  const fin = hasta ? new Date(new Date(hasta).getTime() - 1000) : null;
  return `Bonif. ${Number(pct)}% · ${fin ? 'hasta ' + fmt(fin) : 'siempre'}`;
}

// BONIFICAR A UN BOLICHE. Rige desde ahora; si había otra, la reemplaza (queda en el
// historial). No toca cargos ya emitidos.
function ModalBonificacion({ org, api, onClose, onGuardado }) {
  const [data, setData] = useState(null);
  const [pct, setPct] = useState('100');
  const [motivo, setMotivo] = useState('');
  const [siempre, setSiempre] = useState(true);
  const [hasta, setHasta] = useState(dateInput(plusDays(90)));
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/superadmin/orgs/${org.id}/bonificaciones`).then(setData).catch(e => alert(e.message));
  }, [org.id]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  async function guardar() {
    const p = Number(pct);
    if (!Number.isFinite(p) || p <= 0 || p > 100) { alert('El porcentaje va de 1 a 100.'); return; }
    const resumen = `${p}% ${siempre ? 'para siempre' : 'hasta el ' + fmt(hasta + 'T12:00:00')}`;
    if (!window.confirm(`¿Bonificar a "${org.name}" ${resumen}?\n\nRige desde ahora${data?.vigente ? ' y reemplaza la bonificación actual' : ''}. Los cargos ya emitidos no cambian.`)) return;
    setBusy(true);
    try {
      await api.post(`/api/superadmin/orgs/${org.id}/bonificacion`, { pct: p, motivo, hasta: siempre ? null : hasta });
      load(); onGuardado();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function quitar() {
    if (!window.confirm(`¿Quitar la bonificación de "${org.name}"? Desde ahora paga el precio completo. Lo ya emitido no cambia.`)) return;
    setBusy(true);
    try { await api.del(`/api/superadmin/orgs/${org.id}/bonificacion`); load(); onGuardado(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  const v = data?.vigente;
  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, padding: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <div>
            <div className="lb-eyebrow" style={{ color: '#EC268F' }}>Bonificar · {org.slug}</div>
            <div className="lb-h" style={{ fontSize: 18 }}>{org.name}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5, marginBottom: 14 }}>
          Descuento sobre lo que consuma: el abono y las entradas. El boliche ve el precio lleno y lo bonificado.
          Un cargo bonificado al 100% no cuenta como deuda.
        </div>

        {v ? (
          <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(46,204,113,0.10)', border: '1px solid rgba(46,204,113,0.35)', fontSize: 13, color: '#2ecc71', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ flex: 1 }}>Vigente: <b>{bonifTexto(v.pct, v.hasta)}</b>{v.motivo ? <span style={{ color: 'var(--ink-700)' }}> — {v.motivo}</span> : ''}</span>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={busy} onClick={quitar}>Quitar</button>
          </div>
        ) : data && <div style={{ fontSize: 13, color: 'var(--ink-700)', marginBottom: 16 }}>Hoy no tiene bonificación.</div>}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--ink-700)', width: 90 }}>Porcentaje</span>
          <input className="lb-input" style={{ width: 90 }} type="number" min="1" max="100" value={pct} onChange={e => setPct(e.target.value)} />
          <span style={{ color: 'var(--ink-600)' }}>%</span>
          {['100', '50', '25'].map(x => <button key={x} className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setPct(x)}>{x}%</button>)}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-700)', width: 90 }}>Motivo</span>
          <input className="lb-input" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Club amigo, promo de lanzamiento… (solo lo ves vos)" />
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--ink-700)', width: 90 }}>Vigencia</span>
          <label style={{ fontSize: 13, color: 'var(--ink-700)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" checked={siempre} onChange={() => setSiempre(true)} /> Para siempre
          </label>
          <label style={{ fontSize: 13, color: 'var(--ink-700)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" checked={!siempre} onChange={() => setSiempre(false)} /> Hasta el
            <input className="lb-input" style={{ width: 'auto' }} type="date" value={hasta} min={dateInput(new Date())} disabled={siempre} onChange={e => setHasta(e.target.value)} />
          </label>
        </div>
        {!siempre && <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: -10, marginBottom: 16 }}>Incluye ese día. El abono de un mes se bonifica si el mes cierra antes de esa fecha.</div>}

        <button className="lb-btn lb-btn--primary lb-btn--block" disabled={busy} onClick={guardar}>{busy ? '…' : v ? 'Reemplazar bonificación' : 'Bonificar'}</button>

        {(data?.historial || []).filter(b => !b.vigente).length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Historial</div>
            {data.historial.filter(b => !b.vigente).map(b => (
              <div key={b.id} style={{ fontSize: 12, color: 'var(--ink-600)', padding: '5px 0', borderTop: '1px solid var(--line)' }}>
                {Number(b.pct)}% · {fmt(b.desde)} → {b.hasta ? fmt(new Date(new Date(b.hasta).getTime() - 1000)) : 'sin fin'}{b.motivo ? ` · ${b.motivo}` : ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// COBRO A LOS BOLICHES: pagan desde Mi plan con Mercado Pago y el cargo queda pagado solo.
// Acá se ve a qué cuenta entra la plata (MP_COBROS_ACCESS_TOKEN). Ya no hay transferencia:
// era plata para conciliar a mano. Para una excepción queda "Marcar pagado" en Cobros.
function DatosCobro({ api }) {
  const [mp, setMp] = useState(null);
  useEffect(() => { api.get('/api/superadmin/cobro-mp').then(setMp).catch(e => setMp({ configurado: true, ok: false, error: e.message })); }, []); // eslint-disable-line
  if (!mp) return null;
  return (
    <div className="lb-card" style={{ padding: '18px 20px', marginTop: 20, maxWidth: 640 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Cómo te pagan los boliches</div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 12, lineHeight: 1.5 }}>Desde <b style={{ color: '#fff' }}>Mi plan</b>, con Mercado Pago. El cargo queda pagado solo y te llega un email. Para un pago por otro medio, marcalo a mano en Cobros.</div>
      <div style={{ fontSize: 13, padding: '10px 12px', borderRadius: 6, border: '1px solid var(--line)', lineHeight: 1.5,
        color: !mp.configurado ? 'var(--ink-700)' : mp.ok ? '#2ecc71' : 'var(--red-400)' }}>
        {!mp.configurado
          ? 'Pago con Mercado Pago: apagado. Falta la variable MP_COBROS_ACCESS_TOKEN en Railway; los boliches no tienen cómo pagar desde el panel.'
          : mp.ok
            ? <>Pago con Mercado Pago: <b>activo</b>. La plata entra a la cuenta <b style={{ color: '#fff' }}>{mp.nickname || mp.id}</b>{mp.nombre ? ' (' + mp.nombre + ')' : ''}.</>
            : <>Pago con Mercado Pago: <b>no funciona</b>. {mp.error}</>}
      </div>
    </div>
  );
}
